import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Message } from "./Message";
import { LoadingIndicator } from "./LoadingIndicator";
import { ImageModal } from "./ImageModal";

export const ChatView = forwardRef(
  ({ chatHistory, isLoading, scrollContainerRef }, ref) => {
    const lastUserMessageRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const prevChatLengthRef = useRef(0);

    const scrollToUserMessage = useCallback(() => {
      if (!lastUserMessageRef.current || !scrollContainerRef?.current) return;

      const container = scrollContainerRef.current;
      const messageElement = lastUserMessageRef.current;
      const targetScroll = messageElement.offsetTop - 20;

      container.scrollTo({ top: targetScroll, behavior: "instant" });
    }, [scrollContainerRef]);

    useEffect(() => {
      const prevLength = prevChatLengthRef.current;
      const currentLength = chatHistory.length;

      if (currentLength <= prevLength) {
        prevChatLengthRef.current = currentLength;
        return;
      }

      if (currentLength >= 2) {
        const lastMessage = chatHistory[currentLength - 1];
        const secondLastMessage = chatHistory[currentLength - 2];

        const isNewUserMessage =
          secondLastMessage?.role === "user" &&
          lastMessage?.role === "model" &&
          lastMessage?.parts?.[0]?.text === "";

        if (isNewUserMessage) {
          requestAnimationFrame(() => {
            requestAnimationFrame(scrollToUserMessage);
          });
        }
      }

      prevChatLengthRef.current = currentLength;
    }, [chatHistory, scrollToUserMessage]);

    useImperativeHandle(
      ref,
      () => ({
        scrollLastMessageToTop: () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(scrollToUserMessage);
          });
        },
      }),
      [scrollToUserMessage]
    );

    const handleImageClick = useCallback((file) => {
      if (file.type?.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        setSelectedImage({ file, url: imageUrl });
      }
    }, []);

    const closeImageModal = useCallback(() => {
      if (selectedImage?.url) {
        URL.revokeObjectURL(selectedImage.url);
      }
      setSelectedImage(null);
    }, [selectedImage]);

    const shouldShowLoadingIndicator = () => {
      if (!isLoading) return false;
      if (chatHistory.length === 0) return true;

      const lastMessage = chatHistory[chatHistory.length - 1];
      return (
        lastMessage?.role === "user" ||
        (lastMessage?.role === "model" && lastMessage?.parts[0]?.text === "")
      );
    };

    return (
      <div className="w-full max-w-4xl mx-auto p-4">
        <div className="space-y-8">
          {chatHistory.map((msg, index) => {
            const isLastUserMessage =
              msg.role === "user" && index === chatHistory.length - 2;
            const isStreamingMessage =
              isLoading &&
              index === chatHistory.length - 1 &&
              msg.role === "model";

            return (
              <div
                key={`${msg.role}-${index}`}
                ref={isLastUserMessage ? lastUserMessageRef : null}
              >
                <Message
                  message={msg}
                  onImageClick={handleImageClick}
                  isStreaming={isStreamingMessage}
                />
              </div>
            );
          })}

          {shouldShowLoadingIndicator() && <LoadingIndicator />}
        </div>

        <ImageModal selectedImage={selectedImage} onClose={closeImageModal} />
      </div>
    );
  }
);

ChatView.displayName = "ChatView";
